import React, { useState, useEffect } from 'react';
import './accessibilityControls.css';

interface AccessibilitySettings {
  contrast: number;
  brightness: number;
  fontSize: number;
  grayscale: boolean;
}

const AccessibilityControls: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [settings, setSettings] = useState<AccessibilitySettings>({
    contrast: 100,
    brightness: 100,
    fontSize: 100,
    grayscale: false,
  });

  useEffect(() => {
    const saved = localStorage.getItem('accessibility-settings');
    if (saved) {
      const parsed = JSON.parse(saved);
      setSettings(parsed);
      applySettings(parsed);
    }
  }, []);

  const applySettings = (newSettings: AccessibilitySettings) => {
    const root = document.documentElement;
    
    // Apply contrast
    root.style.filter = `contrast(${newSettings.contrast}%) brightness(${newSettings.brightness}%) ${newSettings.grayscale ? 'grayscale(100%)' : ''}`;
    
    // Apply font size
    const baseFontSize = 16; // base font size in px
    const newSize = (baseFontSize * newSettings.fontSize) / 100;
    root.style.fontSize = `${newSize}px`;
  };

  const handleChange = (key: keyof AccessibilitySettings, value: number | boolean) => {
    const newSettings = { ...settings, [key]: value };
    setSettings(newSettings);
    applySettings(newSettings);
    localStorage.setItem('accessibility-settings', JSON.stringify(newSettings));
  };

  const handleReset = () => {
    const defaultSettings: AccessibilitySettings = {
      contrast: 100,
      brightness: 100,
      fontSize: 100,
      grayscale: false,
    };
    setSettings(defaultSettings);
    applySettings(defaultSettings);
    localStorage.setItem('accessibility-settings', JSON.stringify(defaultSettings));
  };

  return (
    <div className="accessibility-controls">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="accessibility-toggle header__top__row--visual-impairment-icon"
        aria-label="Налаштування доступності"
        title="Налаштування доступності"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="currentColor"
          width="32"
          height="32"
        >
          <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" />
        </svg>
      </button>

      {isOpen && (
        <>
          <div className="accessibility-overlay" onClick={() => setIsOpen(false)} />
          <div className="accessibility-panel">
            <div className="accessibility-header">
              <h3>Доступність</h3>
              <button
                onClick={() => setIsOpen(false)}
                className="close-button"
                aria-label="Закрити"
              >
                ✕
              </button>
            </div>

            <div className="accessibility-content">
              <div className="accessibility-group">
                <h4>Контрастність</h4>
                <div className="control-item">
                  <div className="control-header">
                    <span className="control-label">Рівень контрасту</span>
                    <span className="control-value">{settings.contrast}%</span>
                  </div>
                  <input
                    type="range"
                    min="50"
                    max="200"
                    step="5"
                    value={settings.contrast}
                    onChange={(e) => handleChange('contrast', parseInt(e.target.value))}
                    className="control-range"
                  />
                  <div className="control-labels">
                    <span>50%</span>
                    <span>200%</span>
                  </div>
                </div>
              </div>

              <div className="accessibility-group">
                <h4>Яскравість</h4>
                <div className="control-item">
                  <div className="control-header">
                    <span className="control-label">Рівень яскравості</span>
                    <span className="control-value">{settings.brightness}%</span>
                  </div>
                  <input
                    type="range"
                    min="50"
                    max="150"
                    step="5"
                    value={settings.brightness}
                    onChange={(e) => handleChange('brightness', parseInt(e.target.value))}
                    className="control-range"
                  />
                  <div className="control-labels">
                    <span>50%</span>
                    <span>150%</span>
                  </div>
                </div>
              </div>

              <div className="accessibility-group">
                <h4>Розмір шрифту</h4>
                <div className="control-item">
                  <div className="control-header">
                    <span className="control-label">Масштаб тексту</span>
                    <span className="control-value">{settings.fontSize}%</span>
                  </div>
                  <input
                    type="range"
                    min="80"
                    max="200"
                    step="10"
                    value={settings.fontSize}
                    onChange={(e) => handleChange('fontSize', parseInt(e.target.value))}
                    className="control-range"
                  />
                  <div className="control-labels">
                    <span>80%</span>
                    <span>200%</span>
                  </div>
                </div>
              </div>

              <div className="accessibility-group">
                <h4>Колірна схема</h4>
                <div className="control-item">
                  <label className="grayscale-toggle">
                    <input
                      type="checkbox"
                      checked={settings.grayscale}
                      onChange={(e) => handleChange('grayscale', e.target.checked)}
                    />
                    <span className="toggle-label">
                      Чорно-білий режим (без кольорів)
                    </span>
                  </label>
                </div>
              </div>

              <div className="accessibility-actions">
                <button onClick={handleReset} className="reset-button">
                  Скинути налаштування
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AccessibilityControls;
